<template>
  <nav class="app-navbar">
    <ProgressBar v-if="appStore.appBarLoadingCount > 0" class="w-full absolute" />
    <div class="app-navbar-inner-container">
      <div class="hamburger-icon-slot">
        <button
          data-drawer-target="offcanvas-sidemenu"
          data-drawer-toggle="offcanvas-sidemenu"
          type="button"
          class="inline-flex items-center text-sm text-gray-500 rounded-lg xl:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
        >
          <HamburgerIcon class="w-10 h-10 text-gray-800 dark:text-icon" />
        </button>
      </div>
      <div class="dh-logo-slot">
        <a href="javascript:void(0);" class="flex ml-3 xl:hidden">
          <img src="../../assets/branch-logo.svg" class="max-h-11 max-w-[87px]" alt="Digital Hands Logo" />
        </a>
      </div>
      <div class="tenant-name-slot">
        <div v-if="isTenantNameVisible && !isLoadingRelatedTenants">
          {{ tenantName }}
        </div>
      </div>
      <div class="user-profile-slot">
        <div class="flex items-center">
          <div class="flex items-center gap-1 xl:gap-3">
            <AppBarNotificationButton />
            <ProfileMenu />
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { intersection, isEmpty } from 'lodash';
import { useRoute, useRouter } from 'vue-router';

import ProgressBar from '../ProgressBar.vue';
import HamburgerIcon from '@/components/icons/HamburgerIcon.vue';
import { useAppStore } from '../../stores/app.store';
import AppBarNotificationButton from '../notification/AppBarNotificationButton.vue';
import ProfileMenu from './ProfileMenu.vue';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { AccessPolicies, AccessPolicy } from '@/entities/role.entity';

import { MODULES } from '@/app-constants/module.constants';
import useMultitenancy from '@/hooks/useMultitenancy';
import { ROUTER } from '@/app-constants/router.constant';

const props = withDefaults(
  defineProps<{
    showTenantName?: boolean;
  }>(),
  {
    showTenantName: true,
  },
);

const { impersonatedUser, impersonatedTenant } = useImpersonationInfo();

const isTenantNameVisible = ref(true);
const router = useRouter();
const route = useRoute();

const tenantName = ref(impersonatedTenant.tenantName);

const checkAccessList = [
  MODULES.HOME.moduleName,
  MODULES.MY_CASES.moduleName,
  MODULES.DASHBOARDS_CISO.moduleName,
  MODULES.DASHBOARDS_MITRE.moduleName,
  MODULES.DASHBOARDS_EDR.moduleName,
  MODULES.DASHBOARDS_EDR_CROWDSTRIKE.moduleName,
  MODULES.DASHBOARDS_SIEM.moduleName,
  MODULES.DASHBOARDS_VULNERABILITY.moduleName,
  MODULES.CASES.moduleName,
  MODULES.FILE_CENTER.moduleName,
  MODULES.ACTIVITIES.moduleName,
  MODULES.SERVICES.moduleName,
  MODULES.MY_LINKS.moduleName,
  MODULES.USER_MANAGEMENT.moduleName,
];

const shouldFetchTenancyDropdown = computed(() => {
  const currentRoute = router.currentRoute.value;
  const accessPolicy = currentRoute.meta?.accessPolicy as AccessPolicy | AccessPolicies;

  return checkAccessList.includes((accessPolicy as AccessPolicy)?.moduleName);
});

const {
  isEmptyChildTenants,
  isLoadingRelatedTenants,
  hasRelatedReadPermission,
  hasSelfReadPermission,
  routeModules,
  isMultitenancyEnabled,
  tenantDropdownOptions,
} = useMultitenancy({
  shouldRun: shouldFetchTenancyDropdown.value,
});

const SERVICE_NOW_DETAIL_KEY_ALLOWED_LIST = [
  ROUTER.CUSTOMER.SERVICENOW_DETAIL_PAGE.KEY,
  ROUTER.PARTNER.CUSTOMER.SERVICENOW_DETAIL_PAGE.KEY,
];

const isCustomerServiceNowDetailPath = computed(() =>
  SERVICE_NOW_DETAIL_KEY_ALLOWED_LIST.includes(route.name as string),
);

const shouldTenantNameVisible = (): void => {
  if (false === route.meta.showTenantName) {
    isTenantNameVisible.value = false;
    return;
  }

  if (true === route.meta.showTenantName) {
    isTenantNameVisible.value = true;
    return;
  }

  // Handle for Cases detail only
  if (isCustomerServiceNowDetailPath.value) {
    isTenantNameVisible.value = true;
    return;
  }

  if (!props.showTenantName) {
    isTenantNameVisible.value = false;
    return;
  }

  if (!isMultitenancyEnabled.value && hasSelfReadPermission.value) {
    isTenantNameVisible.value = true;
    return;
  }

  if (hasRelatedReadPermission.value && !hasSelfReadPermission.value && isEmptyChildTenants.value) {
    isTenantNameVisible.value = false;
    return;
  }

  if (intersection(checkAccessList, Array.from(new Set(routeModules.value))).length > 0) {
    if (isLoadingRelatedTenants.value) {
      nextTick(() => {
        isTenantNameVisible.value = false;
      });
      return;
    }
    nextTick(() => {
      isTenantNameVisible.value = isEmptyChildTenants.value || !hasRelatedReadPermission.value;
    });
    return;
  }

  nextTick(() => {
    isTenantNameVisible.value = true;
  });
};

watch(
  [
    (): unknown => route.fullPath,
    (): unknown => isLoadingRelatedTenants.value,
    (): unknown => isCustomerServiceNowDetailPath.value,
  ],
  () => {
    if (isCustomerServiceNowDetailPath.value) {
      const currentTenantId = route.query?.tenantId;
      const currentTenant = tenantDropdownOptions.value?.find((t) => t.id === currentTenantId);

      if (!isEmpty(currentTenant)) {
        tenantName.value = currentTenant.name;
      } else {
        tenantName.value = '';
      }
    } else {
      tenantName.value = impersonatedTenant.tenantName;
    }
  },
  {
    immediate: true,
  },
);

watch(
  [
    (): unknown => route.fullPath,
    (): unknown => props.showTenantName,
    (): unknown => isLoadingRelatedTenants.value,
    (): unknown => isEmptyChildTenants.value,
    (): unknown => impersonatedUser,
  ],
  shouldTenantNameVisible,
  {
    immediate: true,
  },
);

const appStore = useAppStore();
</script>
